"use client";

import Link from "next/link";

import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";

import { EmptyState } from "@/components/states/empty-state";
import { QueryBoundary } from "@/components/states/query-boundary";
import { Skeleton } from "@/components/states/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { usePlatformStats } from "@/features/jobs/hooks/use-jobs";
import type {
  DeviceEarner,
  PlatformStats,
  ReferralEarner,
  StatsDevice,
  StatsJob,
  SuccessRateCell,
} from "@/features/jobs/types";

/** Compact leaderboard rank chip. Top three are tinted; the rest stay neutral
 * so the eye lands on the leaders first. */
function RankBadge({ rank }: { rank: number }) {
  return (
    <span
      className={cn(
        "inline-flex size-7 items-center justify-center rounded-md border font-mono text-xs font-semibold tabular-nums",
        rank <= 3
          ? "border-transparent bg-primary text-primary-foreground"
          : "border-hairline bg-elevated text-muted-foreground",
      )}
    >
      {rank}
    </span>
  );
}

/** A titled statistics section with an eyebrow and optional description. */
function StatsSection({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <p className="font-mono text-[0.7rem] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          {eyebrow}
        </p>
        <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function deviceLabel(brand: string, model: string) {
  return [brand, model].filter(Boolean).join(" ");
}

function TopDeviceEarnings({ earners }: { earners: DeviceEarner[] }) {
  if (earners.length === 0) return null;
  return (
    <StatsSection eyebrow="Last 30 days" title="Top device earnings">
      <div className="overflow-hidden rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="bg-elevated hover:bg-elevated">
              <TableHead className="w-12">Rank</TableHead>
              <TableHead>Device</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead className="text-right">Tasks</TableHead>
              <TableHead className="text-right">Earnings</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {earners.map((d, i) => (
              <TableRow key={d.deviceId || `${d.ownerUsername}-${i}`}>
                <TableCell>
                  <RankBadge rank={i + 1} />
                </TableCell>
                <TableCell>
                  <div className="font-medium text-primary-text">
                    {d.deviceName || "Unknown device"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {deviceLabel(d.brand, d.model)}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {d.ownerUsername || "—"}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {d.taskCount}
                </TableCell>
                <TableCell className="text-right font-mono font-semibold tabular-nums text-success-text">
                  {formatCurrency(d.totalEarnings)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </StatsSection>
  );
}

/** Color the successful-task count by the device/job success rate. */
function cellColor(rate: number) {
  if (rate < 30) return "text-rate-poor";
  if (rate < 50) return "text-rate-low";
  if (rate < 70) return "text-rate-mid";
  return "text-rate-high";
}

function MatrixCell({ cell }: { cell?: SuccessRateCell }) {
  if (!cell || cell.totalTasks === 0) {
    return <span className="text-muted-foreground">–</span>;
  }
  return (
    <span
      className={cn(
        "cursor-help font-mono font-semibold tabular-nums",
        cellColor(cell.successRate),
      )}
      title={`Success rate: ${cell.successRate.toFixed(1)}% · Failed ${cell.failedTasks} / Total ${cell.totalTasks}`}
    >
      {cell.successfulTasks}
    </span>
  );
}

function SuccessMatrix({
  jobs,
  devices,
  matrix,
}: {
  jobs: StatsJob[];
  devices: StatsDevice[];
  matrix: PlatformStats["matrix"];
}) {
  return (
    <StatsSection
      eyebrow="Device performance"
      title="Latest jobs — device performance"
      description="Successful task count for the top performing devices across the latest device-agent jobs."
    >
      <div className="overflow-hidden rounded-lg border border-border">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow className="bg-elevated hover:bg-elevated">
              <TableHead className="sticky left-0 z-10 bg-elevated">
                Device / Job
              </TableHead>
              {jobs.map((job) => (
                <TableHead key={job.jobId} className="min-w-[140px] text-center">
                  <Link
                    href={`/job_details?job_id=${job.jobId}`}
                    className="line-clamp-2 normal-case text-primary-text hover:underline"
                    title={job.title}
                  >
                    {job.title}
                  </Link>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices.map((device) => (
              <TableRow key={device.remoteDeviceId}>
                <TableCell className="sticky left-0 z-10 bg-card">
                  <div className="font-medium text-primary-text">
                    {device.name || "Unknown"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {deviceLabel(device.brand, device.model)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {device.ownerUsername}
                  </div>
                </TableCell>
                {jobs.map((job) => (
                  <TableCell
                    key={`${device.remoteDeviceId}-${job.jobId}`}
                    className="text-center"
                  >
                    <MatrixCell
                      cell={matrix[job.jobId]?.[device.remoteDeviceId]}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </StatsSection>
  );
}

function TopReferralEarners({ earners }: { earners: ReferralEarner[] }) {
  if (earners.length === 0) return null;
  return (
    <StatsSection eyebrow="Last 30 days" title="Top referral earners">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {earners.map((earner, i) => (
          <div
            key={earner.username || i}
            className="flex items-center gap-3 rounded-lg border border-hairline bg-elevated p-3"
          >
            <RankBadge rank={i + 1} />
            <div className="min-w-0">
              <div className="truncate font-medium">{earner.username}</div>
              <div className="font-mono text-lg font-semibold tabular-nums text-success-text">
                {formatCurrency(earner.totalEarnings)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </StatsSection>
  );
}

/** /jobs "Platform Stats" tab — top earners + the device/job success matrix. */
export function PlatformStatsView() {
  const query = usePlatformStats();

  return (
    <QueryBoundary
      query={query}
      loadingFallback={
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      }
      isEmpty={(data) => data.jobs.length === 0 || data.devices.length === 0}
      emptyFallback={
        <EmptyState
          title="No automation data available yet"
          description="Device performance and earnings appear once automation tasks have run."
        />
      }
      errorTitle="We couldn't load platform statistics"
    >
      {(data) => (
        <div className="space-y-10">
          <TopDeviceEarnings earners={data.topDeviceEarners} />
          <SuccessMatrix
            jobs={data.jobs}
            devices={data.devices}
            matrix={data.matrix}
          />
          <TopReferralEarners earners={data.topReferralEarners} />
        </div>
      )}
    </QueryBoundary>
  );
}
